import React from 'react';
import { Group, Circle, RegularPolygon } from 'react-konva';
import Konva from 'konva';
import {
  portColor,
  portLocalPosition,
  projectPointToBoxEdge,
  type Port,
  type PortBox,
  type PortEdge,
  type PortParentType,
} from '../../utils/ports';

export type PortMove = { rx: number; ry: number; edge: PortEdge; direction: number };

type Props = {
  ports: Port[];
  /** Local, unrotated box the ports live on (in the parent group's space). */
  box: PortBox;
  /** When true, ports can be dragged along the boundary. */
  editable: boolean;
  /** Current canvas zoom, so port markers stay a constant screen size. */
  zoom: number;
  /** Highlight the selected load port in the Properties panel. */
  selectedPortId?: string | null;
  /** Fired when the user clicks a port (selection for Properties). */
  onPortClick?: (portId: string) => void;
  /** Whether the owner is an equipment overlay instance or a layout stocker. */
  parentType?: PortParentType;
  /** Live-preview callback fired every frame while a port is dragged. */
  onPortDragMove?: (portId: string, move: PortMove) => void;
  /** Commit callback fired when a port drag ends. */
  onPortDragEnd?: (portId: string, move: PortMove) => void;
};

/**
 * Renders connection ports and, when editable, lets the user drag them while
 * keeping each port projected onto the nearest edge of `box`. Shared by
 * Equipment overlays and layout Stockers. Agnostic to parent rotation/scale:
 * works in the parent group's local space; Konva applies the group's transform.
 */
const PortLayer: React.FC<Props> = ({
  ports,
  box,
  editable,
  zoom,
  selectedPortId,
  onPortClick,
  onPortDragMove,
  onPortDragEnd,
  parentType,
}) => {
  const r = 4 / Math.max(zoom, 1e-6);

  const projectNode = (node: Konva.Node): PortMove => {
    const local = { x: node.x(), y: node.y() };
    return projectPointToBoxEdge(local.x, local.y, box);
  };

  return (
    <>
      {ports.map((port) => {
        const lp = portLocalPosition(port, box);
        const color = portColor(port, parentType);
        const selected = selectedPortId === port.id;
        const canDrag = editable || selected;
        return (
          <Group
            key={port.id}
            x={lp.x}
            y={lp.y}
            draggable={canDrag}
            onMouseDown={(e) => {
              if (canDrag || onPortClick) e.cancelBubble = true;
            }}
            onClick={(e) => {
              e.cancelBubble = true;
              onPortClick?.(port.id);
            }}
            onTap={(e) => {
              e.cancelBubble = true;
              onPortClick?.(port.id);
            }}
            onMouseEnter={(e) => {
              e.cancelBubble = true;
              const stage = e.target.getStage();
              if (stage) stage.container().style.cursor = canDrag ? 'grab' : 'pointer';
            }}
            onMouseLeave={(e) => {
              const stage = e.target.getStage();
              if (stage) stage.container().style.cursor = 'default';
            }}
            onDragMove={(e) => {
              e.cancelBubble = true;
              // Constrain the port to the perimeter live: snap the dragged node
              // back onto the nearest edge every frame.
              const move = projectNode(e.target);
              const snapped = portLocalPosition({ rx: move.rx, ry: move.ry }, box);
              e.target.position(snapped);
              onPortDragMove?.(port.id, move);
            }}
            onDragEnd={(e) => {
              e.cancelBubble = true;
              const move = projectNode(e.target);
              const snapped = portLocalPosition({ rx: move.rx, ry: move.ry }, box);
              e.target.position(snapped);
              onPortDragEnd?.(port.id, move);
            }}
          >
            <Circle
              radius={selected || editable ? r * 1.6 : r}
              fill={color}
              stroke={selected ? '#fbbf24' : '#ffffff'}
              strokeWidth={(selected ? 2.4 : 1.2) / Math.max(zoom, 1e-6)}
            />
            <RegularPolygon
              sides={3}
              radius={r}
              rotation={port.direction + 90}
              x={Math.cos((port.direction * Math.PI) / 180) * r * 2}
              y={Math.sin((port.direction * Math.PI) / 180) * r * 2}
              fill={color}
              listening={false}
            />
          </Group>
        );
      })}
    </>
  );
};

export default PortLayer;
